import React, { useEffect, useMemo, useState } from 'react';
import { TextField } from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete } from '@material-ui/lab';
import { DoorModel, useDoorModelsByNameLazyQuery } from '../../queries';
import { useDebounce } from 'use-debounce';

export type FormModel = {
  id: string;
  name: string;
};

type Props = {
  name: string;
  label: string;
};

export default function ControlledDoorModelAutocomplete({ name, label }: Props) {
  const [selectedModel, setSelectedModel] = useState<string | undefined>(undefined);
  const [debouncedModel] = useDebounce(selectedModel, 300);
  const [getDoorModels, { loading: modelsLoading, data: modelsData }] = useDoorModelsByNameLazyQuery();
  const { control, errors, watch } = useFormContext();

  useEffect(() => {
    if (debouncedModel) {
      getDoorModels({
        variables: {
          name: debouncedModel,
        },
      });
    }
  }, [debouncedModel, getDoorModels]);

  const model = watch(name);

  const options = useMemo(() => {
    const temp = modelsData?.doorModelsByName.map(({ id, name }) => ({ id, name })) ?? [];

    if (model && temp.findIndex((t) => t.id === model.id) === -1) {
      temp.push(model);
    }

    return temp;
  }, [modelsData, model]);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={null}
      render={({ onChange }) => (
        <Autocomplete
          fullWidth
          autoSelect
          autoComplete
          value={model ?? ''}
          selectOnFocus
          disableClearable
          handleHomeEndKeys
          // freeSolo
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }
            return option.name;
          }}
          options={options}
          renderInput={(params) => (
            <TextField {...params} label={label} variant="outlined" fullWidth error={!!errors.destinationCompany} />
          )}
          onInputChange={(_, newInputValue) => {
            setSelectedModel(newInputValue);
          }}
          onChange={(_, newValue: Pick<DoorModel, 'id' | 'name'> | string | null) => {
            if (!newValue) {
              onChange(newValue);
            }

            if (newValue && typeof newValue === 'object') {
              newValue = newValue?.name;
            }

            if (typeof newValue === 'string') {
              const existingOption = modelsData?.doorModelsByName.find((f) => f.name === (newValue as string).trim());
              if (existingOption) {
                onChange({ id: existingOption.id, name: existingOption.name } as FormModel);
              } else {
                onChange(undefined);
              }
            }
          }}
          getOptionSelected={(option, value) => option.id === value.id}
          loading={modelsLoading}
        />
      )}
    />
  );
}
